// Products & Catalogue data layer (Phase 3).
//
// The SINGLE interface between the app and the products schema from
// supabase/migrations/20260629141039_products_catalogue.sql (+ P3-WP1 fixes):
//   products · product_categories
//
// SECURITY MODEL (enforced server-side; mirrored here for clarity):
//   • We NEVER send company_id or product_number — DB triggers own them
//     (company forced from auth.uid(); product_number auto = PRD-00001; both
//     immutable). RLS rejects anything out of company scope.
//   • All database access for products/categories lives in THIS file. Components
//     must not query Supabase directly (no business logic in components).
//
// RETURN-TYPE CONTRACT (consistent across the module):
//   • Collection reads → { data: T[], count: number, page?, pageSize? }
//   • Single read      → T | null
//   • create / update  → the affected T
//   • delete           → void  (throws only on error)
//   • All functions THROW a mapped Error on failure (see toError).

import { supabase } from "./supabaseClient";

/**
 * @typedef {Object} Product
 * @property {string} id
 * @property {string} product_number  Auto, immutable (e.g. "PRD-00001").
 * @property {string} name
 * @property {string|null} sku
 * @property {string|null} barcode
 * @property {string|null} description
 * @property {string|null} category_id
 * @property {string|null} unit
 * @property {number|null} wholesale_price
 * @property {number|null} cost_price
 * @property {number} vat_rate
 * @property {boolean} drs_applicable
 * @property {number} drs_rate
 * @property {number} min_order_qty
 * @property {boolean} in_stock
 * @property {"active"|"inactive"|"archived"} status
 * @property {string|null} notes
 * @property {{id:string,name:string}|null} [product_categories]  Embedded category.
 */

/**
 * @typedef {Object} Category
 * @property {string} id
 * @property {string} name
 * @property {string|null} parent_id
 */

/** @typedef {{ data: T[], count: number, page?: number, pageSize?: number }} Page @template T */

const TABLE = "products";
const CATEGORIES = "product_categories";

/** Default rows per page for paginated reads. */
export const DEFAULT_PAGE_SIZE = 50;

/** Selectable status values (for UI dropdowns + validation parity with the DB CHECK). */
export const PRODUCT_STATUSES = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "archived", label: "Archived" },
];

/** Irish VAT rates (for UI dropdowns). */
export const VAT_RATES = [
  { value: 0, label: "0%" },
  { value: 9, label: "9%" },
  { value: 13.5, label: "13.5%" },
  { value: 23, label: "23%" },
];

/** Sort keys accepted by getProducts({ sortBy }). */
export const PRODUCT_SORTS = [
  { value: "number", label: "Product No." },
  { value: "name", label: "Name" },
  { value: "sku", label: "SKU" },
  { value: "status", label: "Status" },
  { value: "price", label: "Price" },
  { value: "created", label: "Newest" },
];

// Columns the client may write. company_id / product_number / created_by / id /
// doc / timestamps are deliberately absent — the DB owns them.
const PRODUCT_WRITE_FIELDS = [
  "name", "sku", "barcode", "description", "category_id", "unit", "brand",
  "wholesale_price", "retail_price", "cost_price", "vat_rate", "drs_applicable", "drs_rate",
  "min_order_qty", "in_stock", "status", "notes",
];
const CATEGORY_WRITE_FIELDS = ["name", "parent_id"];

// sortBy alias → real column.
const SORT_COLUMNS = { name: "name", number: "product_number", sku: "sku", price: "wholesale_price", created: "created_at", status: "status" };

// Full product projection incl. embedded category (RLS hides foreign categories).
const PRODUCT_SELECT =
  "id, product_number, name, sku, barcode, description, category_id, unit, brand, " +
  "wholesale_price, retail_price, cost_price, vat_rate, drs_applicable, drs_rate, min_order_qty, " +
  "in_stock, status, notes, created_at, updated_at, product_categories ( id, name )";

const CATEGORY_SELECT = "id, name, parent_id, created_at";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}

// Keep only whitelisted keys; coerce "" → null so empty form fields clear values.
const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};

// Strip characters that would break PostgREST's `or()` filter / ilike value.
const sanitizeTerm = (t) => String(t || "").trim().replace(/[%,()]/g, "");

// Map a Supabase/Postgres error to a friendly, consistent Error (preserves .code/.cause).
function toError(error, fallback, entity = "product") {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23505": message = entity === "category" ? "A category with this name already exists." : "A product with this SKU already exists in your company."; break;
    case "23503": message = "The selected category does not exist."; break;
    case "23514": message = "Invalid value — check the status field."; break;
    case "23502": message = "A required field is missing."; break;
    case "PGRST116": message = `${entity[0].toUpperCase()}${entity.slice(1)} not found, or you do not have access to it.`; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message);
  e.code = code;
  e.cause = error;
  return e;
}

// ── Products: reads ──────────────────────────────────────────────────────────

/**
 * List products for the caller's company (RLS-scoped), with search, filtering,
 * sorting and pagination. Embeds each product's category.
 *
 * @param {Object} [opts]
 * @param {string} [opts.search]      Free text matched (ILIKE) against product_number, sku, barcode, name.
 * @param {string} [opts.categoryId]  Restrict to a category id.
 * @param {"all"|"active"|"inactive"|"archived"} [opts.status]  Status filter ("all"/undefined = no filter).
 * @param {number} [opts.page=1]       1-based page number.
 * @param {number} [opts.pageSize=50]  Rows per page.
 * @param {"name"|"number"|"price"|"created"|"status"} [opts.sortBy="name"]  Sort key.
 * @param {"asc"|"desc"} [opts.sortDir="asc"]  Sort direction.
 * @returns {Promise<Page<Product>>} `{ data, count, page, pageSize }` — count is the total matching (all pages).
 * @throws {Error} Mapped error on failure.
 */
export async function getProducts(opts = {}) {
  const { search, categoryId, status, page = 1, pageSize = DEFAULT_PAGE_SIZE, sortBy = "name", sortDir = "asc" } = opts;
  const c = client();
  const column = SORT_COLUMNS[sortBy] || "name";
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const from = (safePage - 1) * safeSize;
  const to = from + safeSize - 1;

  let q = c
    .from(TABLE)
    .select(PRODUCT_SELECT, { count: "exact" })
    .order(column, { ascending: sortDir !== "desc" })
    .range(from, to);

  if (status && status !== "all") q = q.eq("status", status);
  if (categoryId) q = q.eq("category_id", categoryId);
  const term = sanitizeTerm(search);
  if (term) {
    q = q.or(`product_number.ilike.%${term}%,sku.ilike.%${term}%,barcode.ilike.%${term}%,name.ilike.%${term}%`);
  }

  const { data, error, count } = await q;
  if (error) throw toError(error, "Could not load products");
  return { data: data || [], count: count ?? 0, page: safePage, pageSize: safeSize };
}

/**
 * Convenience wrapper around getProducts for a search term.
 * Searches product_number, SKU, barcode and name. Accepts the same options as getProducts.
 *
 * @param {string} term
 * @param {Object} [opts]  Same shape as getProducts opts (minus `search`).
 * @returns {Promise<Page<Product>>}
 * @throws {Error}
 */
export async function searchProducts(term, opts = {}) {
  return getProducts({ ...opts, search: term });
}

/**
 * Fetch a single product by id (RLS-scoped), with embedded category.
 * @param {string} id
 * @returns {Promise<Product|null>} The product, or null if not found / not accessible.
 * @throws {Error}
 */
export async function getProduct(id) {
  const { data, error } = await client().from(TABLE).select(PRODUCT_SELECT).eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load product");
  return data || null;
}

// ── Products: writes ─────────────────────────────────────────────────────────

/**
 * Create a product. company_id and product_number are assigned server-side.
 * @param {Partial<Product>} payload  Only whitelisted fields are sent.
 * @returns {Promise<Product>} The created product (with embedded category).
 * @throws {Error} Mapped error (e.g. duplicate SKU, bad category, invalid status).
 */
export async function createProduct(payload) {
  const { data, error } = await client().from(TABLE).insert(pick(payload, PRODUCT_WRITE_FIELDS)).select(PRODUCT_SELECT).single();
  if (error) throw toError(error, "Could not create product");
  return data;
}

/**
 * Update a product by id. company_id / product_number are immutable (DB-enforced)
 * and never sent. Returns the updated row.
 * @param {string} id
 * @param {Partial<Product>} payload  Only whitelisted fields are sent.
 * @returns {Promise<Product>} The updated product.
 * @throws {Error} Mapped error (incl. "not found / no access" when RLS hides the row).
 */
export async function updateProduct(id, payload) {
  const { data, error } = await client().from(TABLE).update(pick(payload, PRODUCT_WRITE_FIELDS)).eq("id", id).select(PRODUCT_SELECT).single();
  if (error) throw toError(error, "Could not update product");
  return data;
}

/**
 * Delete a product by id. No-op (no error) if the row is not visible to the caller.
 * @param {string} id
 * @returns {Promise<void>}
 * @throws {Error}
 */
export async function deleteProduct(id) {
  const { error } = await client().from(TABLE).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete product");
}

// ── Categories ───────────────────────────────────────────────────────────────

/**
 * List the company's product categories, alphabetically.
 * @returns {Promise<Page<Category>>} `{ data, count }`.
 * @throws {Error}
 */
export async function getProductCategories() {
  const { data, error } = await client().from(CATEGORIES).select(CATEGORY_SELECT).order("name", { ascending: true });
  if (error) throw toError(error, "Could not load categories", "category");
  return { data: data || [], count: (data || []).length };
}

/**
 * Create a category. company_id is assigned server-side.
 * @param {{name:string, parent_id?:string|null}} payload
 * @returns {Promise<Category>}
 * @throws {Error} Mapped error (e.g. duplicate name).
 */
export async function createCategory(payload) {
  const { data, error } = await client().from(CATEGORIES).insert(pick(payload, CATEGORY_WRITE_FIELDS)).select(CATEGORY_SELECT).single();
  if (error) throw toError(error, "Could not create category", "category");
  return data;
}

/**
 * Update a category by id.
 * @param {string} id
 * @param {{name?:string, parent_id?:string|null}} payload
 * @returns {Promise<Category>}
 * @throws {Error}
 */
export async function updateCategory(id, payload) {
  const { data, error } = await client().from(CATEGORIES).update(pick(payload, CATEGORY_WRITE_FIELDS)).eq("id", id).select(CATEGORY_SELECT).single();
  if (error) throw toError(error, "Could not update category", "category");
  return data;
}

/**
 * Delete a category by id. Products referencing it have category_id set to null
 * (ON DELETE SET NULL). No-op if not visible to the caller.
 * @param {string} id
 * @returns {Promise<void>}
 * @throws {Error}
 */
export async function deleteCategory(id) {
  const { error } = await client().from(CATEGORIES).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete category", "category");
}
